import './PdfViewer.scss';
import * as React from 'react';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { createPortal } from 'react-dom';
import { Button } from '@shared/ui-react/Button';
import { FormGroup } from '@shared/ui-react/UtilComponents';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { ProjectPropertyJsonArrayStore } from '@pages/project-settings/shared/ProjectPropertyJsonArrayStore';
import { ImagesService } from '@shared/images/ImagesService';
import { cs } from '@shared/utils/utils-classname';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { FileTreeService } from '@shared/services/FileTreeService';
import { FileServiceClient } from '@shared/files/FileServiceClient';
import axios from 'axios';
import { Notify } from '@shared/ui-native/Notify';
import { Catch } from '@shared/ui-react/Catch';
import { Ajax2 } from '@shared/Ajax';
import { LStorage } from '@shared/Storage';


@observer
export class PdfViewer extends React.Component<{
    pdfUrl: string;
    canEdit?: boolean;
    documentUuid?: string;
    height?: string;

    // form fill
    getRenamedName?: (template: string, description: string, folderUuid: string, latLon: [number, number], doc: IDocument) => string;
    mapMarkerLatLon?: [number, number];
    getUserLatLon?: () => [number, number];
    onSaveSuccess?: (doc: IDocument) => void;
    onClose?: () => void;
    openSavedPdfInFolder?: (documentUuid: string) => void;
    openSavedPdfOnMap?: (documentUuid: string) => void;
}> {
    iframeEl: HTMLIFrameElement;
    @observable iframeLoaded = false;

    // form fill stuff
    @observable isSaving = false;
    @observable hasChanges = true;
    @observable step: 'fill' | 'save' | 'done' = 'fill';
    @observable activeNameTemplate = null;
    @observable manualName: string = null;
    @observable descriptionInput: string = null;
    @observable folderUuid: string = kuura.getRootFolderUuid();
    @observable locationSource: 'marker' | 'template' | 'geolocation' = this.props.mapMarkerLatLon ? 'marker' : 'template';
    @observable templateDocument: IDocument = null;
    @observable savedDocument: IDocument = null;
    @observable isLoadingNameTemplates = false;
    treeData: ITreeData = null;
    nameTemplatesStore = new ProjectPropertyJsonArrayStore<INamingTemplate>(ImagesService.nameTemplatesStorageKey);

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        this.init();
    }

    componentWillUnmount(): void {
        document.removeEventListener("webviewerloaded", this.onWebViewerLoaded);
        this.destroy();
    }

    destroy() {
        // not really needed, memory is cleaned properly anyway
        try {
            const iframeWindow = this.iframeEl?.contentWindow as any;
            if (!iframeWindow?.PDFViewerApplication) return;
            if (iframeWindow.PDFViewerApplicationOptions?.eventBus) {
                iframeWindow.PDFViewerApplicationOptions.eventBus.off('annotationeditorstateschanged');
                iframeWindow.PDFViewerApplicationOptions.eventBus.off('signaturewidgetchanged');
            }

            if (iframeWindow.PDFViewerApplication.pdfDocument) {
                iframeWindow.PDFViewerApplication.pdfDocument.cleanup();
                iframeWindow.PDFViewerApplication.pdfDocument.destroy();
            }

            if (iframeWindow.PDFViewerApplication.pdfViewer) {
                iframeWindow.PDFViewerApplication.pdfViewer.cleanup();
            }

            if (iframeWindow.pdfjsLib && iframeWindow.pdfjsLib.GlobalWorkerOptions.workerPort) {
                iframeWindow.pdfjsLib.GlobalWorkerOptions.workerPort.terminate();
            }

            iframeWindow.PDFViewerApplication.close();
        } catch (e) {
            console.error('error cleaning up PDF viewer', e);
        }
    }

    componentDidUpdate(prevProps: any) {
        if (this.props.pdfUrl !== prevProps.pdfUrl) {
            const iframeWindow = this.iframeEl.contentWindow as any;
            iframeWindow.PDFViewerApplication.open({ url: this.props.pdfUrl });
            this.step = 'fill';
            this.templateDocument = null;
            this.savedDocument = null;
            // this.hasChanges = false;
        }
    }

    async init() {
        const iframeDocument = this.iframeEl.contentDocument || this.iframeEl.contentWindow.document;
        iframeDocument.open();
        iframeDocument.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                    <link rel="stylesheet" href="//web.infrakit.com/lib/pdfjs-5.2.133-legacy-dist/web/viewer.css" />
                    <link rel="resource" type="application/l10n" href="//web.infrakit.com/lib/pdfjs-5.2.133-legacy-dist/web/locale/locale.json" />
                </head>
                <body>
                    <div id="react-root" style="height: 100%; max-height: 99dvh; min-height: 300px; display: none;"></div>
                </body>
            </html>
        `);
        iframeDocument.close();
        this.iframeLoaded = true;

        const loadIFrameScripts = async () => {
            const doc = (this.iframeEl.contentDocument || this.iframeEl.contentWindow.document)
            const el = doc.head;
            doc.getElementById('react-root').style.display = 'block';
            const loadScript = (src: string): Promise<void> => {
                return new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.type = 'module';
                    script.src = src;
                    script.onload = () => resolve();
                    script.onerror = error => reject(error);
                    el.appendChild(script);
                });
            };
            await loadScript("https://web.infrakit.com/lib/pdfjs-5.2.133-legacy-dist/build/pdf.mjs");
            await loadScript("https://web.infrakit.com/lib/pdfjs-5.2.133-legacy-dist/build/pdf.worker.mjs");
            await loadScript("https://web.infrakit.com/lib/pdfjs-5.2.133-legacy-dist/web/viewer.mjs");
        }

        document.addEventListener("webviewerloaded", this.onWebViewerLoaded);

        setTimeout(() => {
            if (iframeDocument.readyState === 'complete') {
                loadIFrameScripts();
            } else {
                iframeDocument.onreadystatechange = loadIFrameScripts;
            }
        }, 100);
    }

    onWebViewerLoaded = (evt) => {
        const iframeWindow = this.iframeEl.contentWindow as any;

        iframeWindow.PDFViewerApplicationOptions.set('enableSignatureEditor', this.props.canEdit);
        iframeWindow.PDFViewerApplicationOptions.set('sidebarViewOnLoad', 0); // 0 = don't open sidebar by default
        iframeWindow.PDFViewerApplicationOptions.set('defaultUrl', this.props.pdfUrl);
        iframeWindow.PDFViewerApplicationOptions.set("localeProperties", {
            lang: kuura.getLanguage()
        });

        iframeWindow.PDFViewerApplicationOptions.set('disableAutoFetch', true);
        iframeWindow.PDFViewerApplicationOptions.set('disableStream', true);

        if (!this.props.canEdit) {
            iframeWindow.PDFViewerApplicationOptions.set('annotationEditorMode', -1); // https://deepwiki.com/mozilla/pdfjs-dist/6.2-advanced-configuration#annotation-modes
            //iframeWindow.PDFViewerApplicationOptions.set('annotationMode', -1);
            iframeWindow.PDFViewerApplicationOptions.set('enableScripting', false);
            iframeWindow.PDFViewerApplicationOptions.set('disableXfa', true);
            try {
                iframeWindow.PDFViewerApplication.appConfig.toolbar.editorToolbar.classList.add('hidden');
            } catch { }
        }

        setTimeout(() => {
            if (this.props.canEdit) {

                iframeWindow.PDFViewerApplicationOptions.eventBus.on('annotationeditorstateschanged', () => this.hasChanges = true);
                iframeWindow.PDFViewerApplicationOptions.eventBus.on('signaturewidgetchanged', () => this.hasChanges = true);
                this.hasChanges = true;
            }
        }, 500);
    }

    openSaveTemplateStep = async () => {
        try {
            this.step = 'save';
            this.isLoadingNameTemplates = true;

            if (!this.treeData) {
                this.treeData = await FileTreeService.fetchFolderTree();
            }

            if (!this.templateDocument) {
                this.templateDocument = await this.fetchDocByUuid(this.props.documentUuid);
                this.folderUuid = this.templateDocument.folder?.uuid;
            }

            if (this.props.getRenamedName) {
                await this.nameTemplatesStore.fetch();
                if (this.nameTemplatesStore.allItems.length === 0) {
                    this.nameTemplatesStore.allItems.push({
                        t: ImagesService.defaultNameTemplate
                    });
                }
                const lastUsedTemplate = LStorage.getItem(this.nameTemplateStorageKey);
                if (lastUsedTemplate && this.nameTemplatesStore.allItems.some(t => t.t === lastUsedTemplate)) {
                    this.activeNameTemplate = lastUsedTemplate;
                }
            }
        } catch (e) {
            Notify.error('Failed to fetch necessary data');
            console.error(e);
            this.step = 'fill';
        } finally {
            this.isLoadingNameTemplates = false;
        }
    }

    nameTemplateStorageKey = `${kuura.getProjectUuid()}_PDF_VIEWER_NAME_TEMPLATE`;
    setNameTemplate = (t: string | null) => {
        this.activeNameTemplate = t;
        LStorage.setItem(this.nameTemplateStorageKey, this.activeNameTemplate);
    }

    render() {
        return (
            <>
                {this.props.canEdit && <div style={{ marginBottom: 20 }}>
                    <nav className='floating-window-toolbar' style={{ justifyContent: 'space-between', alignItems: 'stretch', opacity: this.step === 'fill' ? 1 : 0 }}>
                        <div className="flex-row gap-half">
                            <Button secondary subtle icon='iki iki-arrow-left' onClick={this.props.onClose} style={{ paddingLeft: 20, paddingRight: 20 }} />
                            <Button subtle icon='iki iki-reset' tooltip={__('js_files.fill_template.clear_changes')} onClick={this.clearPdf} />
                        </div>
                        <Button primary onClick={this.openSaveTemplateStep} disabled={!this.hasChanges} style={{ maxWidth: 300 }}>{__('js_files.fill_template.submit_filled_pdf')}</Button>
                    </nav>
                    {this.step === 'done' && <div className="flex-column" style={{ position: 'absolute', inset: '30px 0 0', zIndex: 9 }}>
                        <div className="flex-column gap padded card" style={{ alignItems: 'center', margin: 'auto', flexGrow: 0, minWidth: 'max(40%, 320px)' }}>
                            <h4>{__('js_files.fill_template.pdf_saved')}</h4>
                            <div className="flex-row gap-half" style={{ marginTop: -10, marginBottom: 20 }}>
                                {this.props.openSavedPdfInFolder && <Button icon='iki iki-files' onClick={() => this.props.openSavedPdfInFolder(this.savedDocument.uuid)}>{__('js_global_search.actions.open_in_folder')}</Button>}
                                {this.props.openSavedPdfOnMap && <Button icon='iki iki-pin' onClick={() => this.props.openSavedPdfOnMap(this.savedDocument.uuid)}>{__('js_global_search.actions.open_on_map')}</Button>}
                            </div>

                            <Button primary onClick={() => this.step = 'fill'}>{__('js_files.fill_template.back_to_template')}</Button>
                        </div>
                    </div>}
                    {this.step === 'save' &&
                        <div className="flex-column" style={{ position: 'absolute', inset: '30px 0 0', zIndex: 9 }}>

                            <div className={cs('flex-column gap padded card', { 'overlay-loading': this.isLoadingNameTemplates || this.isSaving })}
                                style={{ margin: 'auto', flexGrow: 0, minWidth: 'max(40%, 320px)' }}>
                                <FormGroup title={__('general.name')}>
                                    <div>
                                        <div className="flex-row">
                                            {this.activeNameTemplate ?
                                                <input type="text" name="name_template" value={this.activeNameTemplate} readOnly style={{ background: 'transparent' }} key="template" />
                                                :
                                                <input type="text" name="name" value={this.manualName} onChange={(e) => this.manualName = e.target.value} maxLength={255} key="manual-name" />
                                            }

                                            {this.props.getRenamedName &&
                                                <Dropdown closeOnBodyClick={true} className="ik-button-secondary--subtle ik-button-small" tooltip={__('js_name_templates.template')}>
                                                    <span><i className="iconf-caret" /></span>
                                                    <ul className="clickable">
                                                        <li onClick={() => this.setNameTemplate(null)} style={{ borderBottom: '2px solid #ccc' }}>
                                                            <input type="checkbox" checked={this.activeNameTemplate === null} />
                                                            <span>{__('js_photo.name.enter_value')}</span>
                                                        </li>
                                                        {this.nameTemplatesStore.allItems.map((t, i) => (
                                                            <li onClick={() => this.setNameTemplate(t.t)} key={i}>
                                                                <input type="checkbox" checked={this.activeNameTemplate === t.t} />
                                                                <span>{t.n?.length > 0 ? t.n : t.t}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </Dropdown>
                                            }
                                        </div>
                                        <Catch><div style={{ marginTop: 10, maxWidth: 400 }}>{this.getRenamedName()}</div></Catch>
                                    </div>
                                </FormGroup>

                                <FormGroup title={__('general.folder')}>
                                    {this.treeData &&
                                        <FiletreeSelectReact selectable="folder" useUuid={true}
                                            treeData={this.treeData} selectedId={this.folderUuid}
                                            onChange={folderUuid => this.folderUuid = folderUuid}
                                            storageKey={'PDfTemplateFill_folderSelectTree'}
                                        />
                                    }
                                </FormGroup>

                                <FormGroup title={__('general.description')}>
                                    <input type="text" name="name" value={this.descriptionInput} onChange={(e) => this.descriptionInput = e.target.value} maxLength={255} key="manual-description" />
                                </FormGroup>

                                <FormGroup title={__('general.position')}>
                                    <label className="radio-label" onClick={() => this.locationSource = 'marker'}>
                                        <input type="radio" checked={this.locationSource === 'marker'} disabled={!this.props.mapMarkerLatLon} /> <span>{__('js_files.fill_template_map_marker_location')}</span>
                                    </label>
                                    <label className="radio-label" onClick={() => this.locationSource = 'template'}>
                                        <input type="radio" checked={this.locationSource === 'template'} /> <span>{__('js_files.fill_template_location')}</span>
                                    </label>
                                    { this.props.getUserLatLon && <label className="radio-label" onClick={() => this.locationSource = 'geolocation'}>
                                        <input type="radio" checked={this.locationSource === 'geolocation'} /> <span>{__('js_files.fill_template_user_position')}</span>
                                    </label> }

                                    <Catch>
                                        <div style={{ marginTop: 5, marginLeft: 5, opacity: 0.7 }}>
                                            <div>lat: {this.selectedLatLon[0].toFixed(4)}</div>
                                            <div>lon: {this.selectedLatLon[1].toFixed(4)}</div>
                                        </div>
                                    </Catch>
                                </FormGroup>

                                <div className="flex-row gap-half" style={{ marginTop: 20, justifyContent: 'space-between' }}>
                                    <Button secondary onClick={() => this.step = 'fill'}>{__('general.cancel')}</Button>
                                    <Button primary onClick={this.upload} disabled={!this.canSubmit()}>{__('general.save')}</Button>
                                </div>
                            </div>
                        </div>
                    }
                </div>
                }

                <iframe ref={el => this.iframeEl = el} className={this.step === 'fill' ? '' : 'blur-disable'} style={{ height: this.props.height ?? '100%', width: '100%' }}></iframe>
                {
                    this.iframeLoaded && this.iframeEl && createPortal(
                        <IframeContent />,
                        (this.iframeEl.contentDocument || this.iframeEl.contentWindow.document).getElementById('react-root')
                    )
                }
            </>
        );
    }

    getRenamedName = () => {
        if (this.activeNameTemplate == null || !this.props.getRenamedName) {
            return null;
        }

        return this.props.getRenamedName(
            this.activeNameTemplate,
            this.descriptionInput,
            this.folderUuid,
            this.selectedLatLon,
            this.templateDocument as any
        )
    }

    get selectedLatLon(): [number, number] {
        if (this.locationSource === 'marker') {
            return this.props.mapMarkerLatLon ?? [0, 0];
        } else if (this.locationSource === 'template') {
            if (this.templateDocument?.geographicPoint) {
                return [this.templateDocument.geographicPoint.lat, this.templateDocument.geographicPoint.lon]
            } else {
                return [0, 0]
            }
        }
        else if (this.locationSource === 'geolocation') {
            return this.props.getUserLatLon() ?? [0, 0];
        }
        return [0, 0]
    }

    getBlob = async () => {
        const iframeWindow = this.iframeEl.contentWindow as any;
        await this.commitCurrentAnnotation();
        const data = await iframeWindow.PDFViewerApplication.pdfDocument.saveDocument();
        return new Blob([data], { type: 'application/pdf' });
    }

    commitCurrentAnnotation = async () => {
        // when we getBlob, but user was in the process of drawing annotation and didn't finish it by switching to some other tool
        // that annotation doesn't get saved, so we have to manually force draw end
        try {
            const iframeWindow = this.iframeEl.contentWindow as any;
            const pdfViewer = iframeWindow.PDFViewerApplication.pdfViewer;
            const currentPageNumber = pdfViewer?.currentPageNumber || 1;
            const currentPageView = pdfViewer?.getPageView(currentPageNumber - 1);

            if (currentPageView?.annotationEditorLayer) {
                const editorLayer = currentPageView.annotationEditorLayer;
                editorLayer.annotationEditorLayer.commitOrRemove();
            }
        } catch (err) {
            console.error('commitCurrentAnnotation', err);
        }
    }

    saveBlob = async () => {
        const blob = await this.getBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'document.pdf';
        link.click();
        URL.revokeObjectURL(url);
    }

    canSubmit = () => {
        return this.activeNameTemplate || this.manualName?.length >= 3
    }

    upload = async () => {
        if (this.isSaving) {
            return;
        }
        this.isSaving = true;
        const blob = await this.getBlob();
        const file = new File([blob], 'document.pdf', { type: 'application/pdf' });
        const finalName = this.activeNameTemplate ? this.getRenamedName() : this.manualName;
        try {
            const s3presignResult = await FileServiceClient.getS3UploadPath(file, {
                fileType: "document",
                filename: finalName.toLowerCase().endsWith('.pdf') ? finalName : finalName + '.pdf',
                description: this.descriptionInput ?? null,
                folderUuid: this.folderUuid,
                reportTemplateId: this.templateDocument?.id,
                lat: this.selectedLatLon[0] !== 0 ? this.selectedLatLon[0] : null,
                lon: this.selectedLatLon[1] !== 0 ? this.selectedLatLon[1] : null
            });
            const result = await axios.put(s3presignResult.uploadUrl, blob, {
                headers: {
                    'Content-Type': file.type,
                    'Content-MD5': s3presignResult.checksumMd5b64
                }
            })
            await this.fakeSleep(5000); // wait for kuura to pull file from s3
            this.step = 'done';
            this.savedDocument = await this.fetchDocByUuid(s3presignResult.document?.uuid);
            if (this.props.onSaveSuccess) {
                this.props.onSaveSuccess(this.savedDocument);
            }
            Notify.success();
        } catch (e) {
            Notify.error(e);
            console.log('e:', e);
        } finally {
            this.isSaving = false;
        }
    }

    fakeSleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

    fetchDocByUuid = (uuid: string) => {
        return Ajax2({
            url: kuura.getContextPath() + '/ajax_document.json',
            params: { uuid }
        }).then(resp => resp.idocuments[0]);
    }

    clearPdf = () => {
        // we need to clear annotations first because there is a hardcoded pdf save-on-close if some annotations exist
        const iframeWindow = this.iframeEl.contentWindow as any;
        const storage = iframeWindow.PDFViewerApplication.pdfDocument.annotationStorage;
        const all = storage.getAll();
        if (all) {
            Object.keys(all).forEach(key => storage.remove(key));
        }
        storage.resetModified();
        iframeWindow.PDFViewerApplication.open({ url: this.props.pdfUrl });
    }
}

const IframeContent = () => {
    return (
        <>

            <div id="outerContainer">

                <div id="sidebarContainer">
                    <div id="toolbarSidebar" className="toolbarHorizontalGroup">
                        <div id="toolbarSidebarLeft">
                            <div id="sidebarViewButtons" className="toolbarHorizontalGroup toggled" role="radiogroup">
                                <button id="viewThumbnail" className="toolbarButton toggled" type="button" data-l10n-id="pdfjs-thumbs-button" role="radio" aria-checked="true" aria-controls="thumbnailView">
                                    <span data-l10n-id="pdfjs-thumbs-button-label"></span>
                                </button>
                                <button id="viewOutline" className="toolbarButton" type="button" data-l10n-id="pdfjs-document-outline-button" role="radio" aria-checked="false" aria-controls="outlineView">
                                    <span data-l10n-id="pdfjs-document-outline-button-label"></span>
                                </button>
                                <button id="viewAttachments" className="toolbarButton" type="button" data-l10n-id="pdfjs-attachments-button" role="radio" aria-checked="false" aria-controls="attachmentsView">
                                    <span data-l10n-id="pdfjs-attachments-button-label"></span>
                                </button>
                                <button id="viewLayers" className="toolbarButton" type="button" data-l10n-id="pdfjs-layers-button" role="radio" aria-checked="false" aria-controls="layersView">
                                    <span data-l10n-id="pdfjs-layers-button-label"></span>
                                </button>
                            </div>
                        </div>

                        <div id="toolbarSidebarRight">
                            <div id="outlineOptionsContainer" className="toolbarHorizontalGroup">
                                <div className="verticalToolbarSeparator"></div>

                                <button id="currentOutlineItem" className="toolbarButton" type="button" disabled data-l10n-id="pdfjs-current-outline-item-button">
                                    <span data-l10n-id="pdfjs-current-outline-item-button-label"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div id="sidebarContent">
                        <div id="thumbnailView">
                        </div>
                        <div id="outlineView" className="hidden">
                        </div>
                        <div id="attachmentsView" className="hidden">
                        </div>
                        <div id="layersView" className="hidden">
                        </div>
                    </div>
                    <div id="sidebarResizer"></div>
                </div>

                <div id="mainContainer">
                    <div className="toolbar">
                        <div id="toolbarContainer">
                            <div id="toolbarViewer" className="toolbarHorizontalGroup">
                                <div id="toolbarViewerLeft" className="toolbarHorizontalGroup">
                                    <button id="sidebarToggleButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-toggle-sidebar-button" aria-expanded="false" aria-haspopup="true" aria-controls="sidebarContainer">
                                        <span data-l10n-id="pdfjs-toggle-sidebar-button-label"></span>
                                    </button>
                                    <div className="toolbarButtonSpacer"></div>
                                    <div className="toolbarButtonWithContainer">
                                        <button id="viewFindButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-findbar-button" aria-expanded="false" aria-controls="findbar">
                                            <span data-l10n-id="pdfjs-findbar-button-label"></span>
                                        </button>
                                        <div className="hidden doorHanger toolbarHorizontalGroup" id="findbar">
                                            <div id="findInputContainer" className="toolbarHorizontalGroup">
                                                <span className="loadingInput end toolbarHorizontalGroup">
                                                    <input id="findInput" className="toolbarField" data-l10n-id="pdfjs-find-input" aria-invalid="false" />
                                                </span>
                                                <div className="toolbarHorizontalGroup">
                                                    <button id="findPreviousButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-find-previous-button">
                                                        <span data-l10n-id="pdfjs-find-previous-button-label"></span>
                                                    </button>
                                                    <div className="splitToolbarButtonSeparator"></div>
                                                    <button id="findNextButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-find-next-button">
                                                        <span data-l10n-id="pdfjs-find-next-button-label"></span>
                                                    </button>
                                                </div>
                                            </div>

                                            <div id="findbarOptionsOneContainer" className="toolbarHorizontalGroup">
                                                <div className="toggleButton toolbarLabel">
                                                    <input type="checkbox" id="findHighlightAll" />
                                                    <label data-l10n-id="pdfjs-find-highlight-checkbox"></label>
                                                </div>
                                                <div className="toggleButton toolbarLabel">
                                                    <input type="checkbox" id="findMatchCase" />
                                                    <label data-l10n-id="pdfjs-find-match-case-checkbox-label"></label>
                                                </div>
                                            </div>
                                            <div id="findbarOptionsTwoContainer" className="toolbarHorizontalGroup">
                                                <div className="toggleButton toolbarLabel">
                                                    <input type="checkbox" id="findMatchDiacritics" />
                                                    <label data-l10n-id="pdfjs-find-match-diacritics-checkbox-label"></label>
                                                </div>
                                                <div className="toggleButton toolbarLabel">
                                                    <input type="checkbox" id="findEntireWord" />
                                                    <label data-l10n-id="pdfjs-find-entire-word-checkbox-label"></label>
                                                </div>
                                            </div>

                                            <div id="findbarMessageContainer" className="toolbarHorizontalGroup" aria-live="polite">
                                                <span id="findResultsCount" className="toolbarLabel"></span>
                                                <span id="findMsg" className="toolbarLabel"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="toolbarHorizontalGroup hiddenSmallView">
                                        <button className="toolbarButton" type="button" id="previous" data-l10n-id="pdfjs-previous-button">
                                            <span data-l10n-id="pdfjs-previous-button-label"></span>
                                        </button>
                                        <div className="splitToolbarButtonSeparator"></div>
                                        <button className="toolbarButton" type="button" id="next" data-l10n-id="pdfjs-next-button">
                                            <span data-l10n-id="pdfjs-next-button-label"></span>
                                        </button>
                                    </div>
                                    <div className="toolbarHorizontalGroup">
                                        <span className="loadingInput start toolbarHorizontalGroup">
                                            <input type="number" id="pageNumber" className="toolbarField" value="1" min="1" data-l10n-id="pdfjs-page-input" />
                                        </span>
                                        <span id="numPages" className="toolbarLabel"></span>
                                    </div>
                                </div>
                                <div id="toolbarViewerMiddle" className="toolbarHorizontalGroup">
                                    <div className="toolbarHorizontalGroup">
                                        <button id="zoomOutButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-zoom-out-button">
                                            <span data-l10n-id="pdfjs-zoom-out-button-label"></span>
                                        </button>
                                        <div className="splitToolbarButtonSeparator"></div>
                                        <button id="zoomInButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-zoom-in-button">
                                            <span data-l10n-id="pdfjs-zoom-in-button-label"></span>
                                        </button>
                                    </div>
                                    <span id="scaleSelectContainer" className="dropdownToolbarButton">
                                        <select id="scaleSelect" data-l10n-id="pdfjs-zoom-select">
                                            <option id="pageAutoOption" value="auto" selected data-l10n-id="pdfjs-page-scale-auto"></option>
                                            <option id="pageActualOption" value="page-actual" data-l10n-id="pdfjs-page-scale-actual"></option>
                                            <option id="pageFitOption" value="page-fit" data-l10n-id="pdfjs-page-scale-fit"></option>
                                            <option id="pageWidthOption" value="page-width" data-l10n-id="pdfjs-page-scale-width"></option>
                                            <option id="customScaleOption" value="custom" disabled hidden data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 0 }'></option>
                                            <option value="0.5" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 50 }'></option>
                                            <option value="0.75" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 75 }'></option>
                                            <option value="1" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 100 }'></option>
                                            <option value="1.25" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 125 }'></option>
                                            <option value="1.5" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 150 }'></option>
                                            <option value="2" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 200 }'></option>
                                            <option value="3" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 300 }'></option>
                                            <option value="4" data-l10n-id="pdfjs-page-scale-percent" data-l10n-args='{ "scale": 400 }'></option>
                                        </select>
                                    </span>
                                </div>
                                <div id="toolbarViewerRight" className="toolbarHorizontalGroup">
                                    <div id="editorModeButtons" className="toolbarHorizontalGroup" role="radiogroup">
                                        <div id="editorSignature" className="toolbarButtonWithContainer" hidden>
                                            <button id="editorSignatureButton" className="toolbarButton" type="button" disabled role="radio" aria-expanded="false" aria-haspopup="true" aria-controls="editorSignatureParamsToolbar" data-l10n-id="pdfjs-editor-signature-button">
                                                <span data-l10n-id="pdfjs-editor-signature-button-label"></span>
                                            </button>
                                            <div className="editorParamsToolbar hidden doorHangerRight menu" id="editorSignatureParamsToolbar">
                                                <div id="addSignatureDoorHanger" className="menuContainer" role="region" data-l10n-id="pdfjs-editor-add-signature-container">
                                                    <button id="editorSignatureAddSignature" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-editor-signature-add-signature-button">
                                                        <span data-l10n-id="pdfjs-editor-signature-add-signature-button-label" className="editorParamsLabel"></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div id="editorHighlight" className="toolbarButtonWithContainer">
                                            <button id="editorHighlightButton" className="toolbarButton" type="button" disabled role="radio" aria-expanded="false" aria-haspopup="true" aria-controls="editorHighlightParamsToolbar" data-l10n-id="pdfjs-editor-highlight-button">
                                                <span data-l10n-id="pdfjs-editor-highlight-button-label"></span>
                                            </button>
                                            <div className="editorParamsToolbar hidden doorHangerRight" id="editorHighlightParamsToolbar">
                                                <div id="highlightParamsToolbarContainer" className="editorParamsToolbarContainer">
                                                    <div id="editorHighlightColorPicker" className="colorPicker">
                                                        <span id="highlightColorPickerLabel" className="editorParamsLabel" data-l10n-id="pdfjs-editor-highlight-colorpicker-label"></span>
                                                    </div>
                                                    <div id="editorHighlightThickness">
                                                        <label className="editorParamsLabel" data-l10n-id="pdfjs-editor-free-highlight-thickness-input"></label>
                                                        <div className="thicknessPicker">
                                                            <input type="range" id="editorFreeHighlightThickness" className="editorParamsSlider" data-l10n-id="pdfjs-editor-free-highlight-thickness-title" value="12" min="8" max="24" step="1" />
                                                        </div>
                                                    </div>
                                                    <div id="editorHighlightVisibility">
                                                        <div className="divider"></div>
                                                        <div className="toggler">
                                                            <label className="editorParamsLabel" data-l10n-id="pdfjs-editor-highlight-show-all-button-label"></label>
                                                            <button id="editorHighlightShowAll" className="toggle-button" type="button" data-l10n-id="pdfjs-editor-highlight-show-all-button" aria-pressed="true" ></button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div id="editorFreeText" className="toolbarButtonWithContainer">
                                            <button id="editorFreeTextButton" className="toolbarButton" type="button" disabled role="radio" aria-expanded="false" aria-haspopup="true" aria-controls="editorFreeTextParamsToolbar" data-l10n-id="pdfjs-editor-free-text-button">
                                                <span data-l10n-id="pdfjs-editor-free-text-button-label"></span>
                                            </button>
                                            <div className="editorParamsToolbar hidden doorHangerRight" id="editorFreeTextParamsToolbar">
                                                <div className="editorParamsToolbarContainer">
                                                    <div className="editorParamsSetter">
                                                        <label className="editorParamsLabel" data-l10n-id="pdfjs-editor-free-text-color-input"></label>
                                                        <input type="color" id="editorFreeTextColor" className="editorParamsColor" />
                                                    </div>
                                                    <div className="editorParamsSetter">
                                                        <label className="editorParamsLabel" data-l10n-id="pdfjs-editor-free-text-size-input"></label>
                                                        <input type="range" id="editorFreeTextFontSize" className="editorParamsSlider" value="10" min="5" max="100" step="1" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div id="editorInk" className="toolbarButtonWithContainer">
                                            <button id="editorInkButton" className="toolbarButton" type="button" disabled role="radio" aria-expanded="false" aria-haspopup="true" aria-controls="editorInkParamsToolbar" data-l10n-id="pdfjs-editor-ink-button">
                                                <span data-l10n-id="pdfjs-editor-ink-button-label"></span>
                                            </button>
                                            <div className="editorParamsToolbar hidden doorHangerRight" id="editorInkParamsToolbar">
                                                <div className="editorParamsToolbarContainer">
                                                    <div className="editorParamsSetter">
                                                        <label className="editorParamsLabel" data-l10n-id="pdfjs-editor-ink-color-input"></label>
                                                        <input type="color" id="editorInkColor" className="editorParamsColor" />
                                                    </div>
                                                    <div className="editorParamsSetter">
                                                        <label className="editorParamsLabel" data-l10n-id="pdfjs-editor-ink-thickness-input"></label>
                                                        <input type="range" id="editorInkThickness" className="editorParamsSlider" value="1" min="1" max="20" step="1" />
                                                    </div>
                                                    <div className="editorParamsSetter">
                                                        <label className="editorParamsLabel" data-l10n-id="pdfjs-editor-ink-opacity-input"></label>
                                                        <input type="range" id="editorInkOpacity" className="editorParamsSlider" value="1" min="0.05" max="1" step="0.05" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div id="editorStamp" className="toolbarButtonWithContainer">
                                            <button id="editorStampButton" className="toolbarButton" type="button" disabled role="radio" aria-expanded="false" aria-haspopup="true" aria-controls="editorStampParamsToolbar" data-l10n-id="pdfjs-editor-stamp-button">
                                                <span data-l10n-id="pdfjs-editor-stamp-button-label"></span>
                                            </button>
                                            <div className="editorParamsToolbar hidden doorHangerRight menu" id="editorStampParamsToolbar">
                                                <div className="menuContainer">
                                                    <button id="editorStampAddImage" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-editor-stamp-add-image-button">
                                                        <span className="editorParamsLabel" data-l10n-id="pdfjs-editor-stamp-add-image-button-label"></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div id="editorModeSeparator" className="verticalToolbarSeparator"></div>

                                    <div className="toolbarHorizontalGroup hiddenMediumView">
                                        <button id="printButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-print-button">
                                            <span data-l10n-id="pdfjs-print-button-label"></span>
                                        </button>

                                        <button id="downloadButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-save-button">
                                            <span data-l10n-id="pdfjs-save-button-label"></span>
                                        </button>
                                    </div>

                                    <div className="verticalToolbarSeparator hiddenMediumView"></div>

                                    <div id="secondaryToolbarToggle" className="toolbarButtonWithContainer">
                                        <button id="secondaryToolbarToggleButton" className="toolbarButton" type="button" data-l10n-id="pdfjs-tools-button" aria-expanded="false" aria-haspopup="true" aria-controls="secondaryToolbar">
                                            <span data-l10n-id="pdfjs-tools-button-label"></span>
                                        </button>
                                        <div id="secondaryToolbar" className="hidden doorHangerRight menu">
                                            <div id="secondaryToolbarButtonContainer" className="menuContainer">
                                                <button id="secondaryOpenFile" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-open-file-button">
                                                    <span data-l10n-id="pdfjs-open-file-button-label"></span>
                                                </button>

                                                <div className="visibleMediumView">
                                                    <button id="secondaryPrint" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-print-button">
                                                        <span data-l10n-id="pdfjs-print-button-label"></span>
                                                    </button>

                                                    <button id="secondaryDownload" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-save-button">
                                                        <span data-l10n-id="pdfjs-save-button-label"></span>
                                                    </button>

                                                </div>

                                                <div className="horizontalToolbarSeparator"></div>

                                                <button id="presentationMode" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-presentation-mode-button">
                                                    <span data-l10n-id="pdfjs-presentation-mode-button-label"></span>
                                                </button>

                                                <a href="#" id="viewBookmark" className="toolbarButton labeled" data-l10n-id="pdfjs-bookmark-button">
                                                    <span data-l10n-id="pdfjs-bookmark-button-label"></span>
                                                </a>

                                                <div id="viewBookmarkSeparator" className="horizontalToolbarSeparator"></div>

                                                <button id="firstPage" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-first-page-button">
                                                    <span data-l10n-id="pdfjs-first-page-button-label"></span>
                                                </button>
                                                <button id="lastPage" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-last-page-button">
                                                    <span data-l10n-id="pdfjs-last-page-button-label"></span>
                                                </button>

                                                <div className="horizontalToolbarSeparator"></div>

                                                <button id="pageRotateCw" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-page-rotate-cw-button">
                                                    <span data-l10n-id="pdfjs-page-rotate-cw-button-label"></span>
                                                </button>
                                                <button id="pageRotateCcw" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-page-rotate-ccw-button">
                                                    <span data-l10n-id="pdfjs-page-rotate-ccw-button-label"></span>
                                                </button>

                                                <div className="horizontalToolbarSeparator"></div>

                                                <div id="cursorToolButtons" role="radiogroup">
                                                    <button id="cursorSelectTool" className="toolbarButton labeled toggled" type="button" data-l10n-id="pdfjs-cursor-text-select-tool-button" role="radio" aria-checked="true">
                                                        <span data-l10n-id="pdfjs-cursor-text-select-tool-button-label"></span>
                                                    </button>
                                                    <button id="cursorHandTool" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-cursor-hand-tool-button" role="radio" aria-checked="false">
                                                        <span data-l10n-id="pdfjs-cursor-hand-tool-button-label"></span>
                                                    </button>
                                                </div>

                                                <div className="horizontalToolbarSeparator"></div>

                                                <div id="scrollModeButtons" role="radiogroup">
                                                    <button id="scrollPage" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-scroll-page-button" role="radio" aria-checked="false">
                                                        <span data-l10n-id="pdfjs-scroll-page-button-label"></span>
                                                    </button>
                                                    <button id="scrollVertical" className="toolbarButton labeled toggled" type="button" data-l10n-id="pdfjs-scroll-vertical-button" role="radio" aria-checked="true">
                                                        <span data-l10n-id="pdfjs-scroll-vertical-button-label"></span>
                                                    </button>
                                                    <button id="scrollHorizontal" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-scroll-horizontal-button" role="radio" aria-checked="false">
                                                        <span data-l10n-id="pdfjs-scroll-horizontal-button-label"></span>
                                                    </button>
                                                    <button id="scrollWrapped" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-scroll-wrapped-button" role="radio" aria-checked="false">
                                                        <span data-l10n-id="pdfjs-scroll-wrapped-button-label"></span>
                                                    </button>
                                                </div>

                                                <div className="horizontalToolbarSeparator"></div>

                                                <div id="spreadModeButtons" role="radiogroup">
                                                    <button id="spreadNone" className="toolbarButton labeled toggled" type="button" data-l10n-id="pdfjs-spread-none-button" role="radio" aria-checked="true">
                                                        <span data-l10n-id="pdfjs-spread-none-button-label"></span>
                                                    </button>
                                                    <button id="spreadOdd" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-spread-odd-button" role="radio" aria-checked="false">
                                                        <span data-l10n-id="pdfjs-spread-odd-button-label"></span>
                                                    </button>
                                                    <button id="spreadEven" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-spread-even-button" role="radio" aria-checked="false">
                                                        <span data-l10n-id="pdfjs-spread-even-button-label"></span>
                                                    </button>
                                                </div>

                                                <div id="imageAltTextSettingsSeparator" className="horizontalToolbarSeparator hidden"></div>
                                                <button id="imageAltTextSettings" type="button" className="toolbarButton labeled hidden" data-l10n-id="pdfjs-image-alt-text-settings-button" aria-controls="altTextSettingsDialog">
                                                    <span data-l10n-id="pdfjs-image-alt-text-settings-button-label"></span>
                                                </button>

                                                <div className="horizontalToolbarSeparator"></div>

                                                <button id="documentProperties" className="toolbarButton labeled" type="button" data-l10n-id="pdfjs-document-properties-button" aria-controls="documentPropertiesDialog">
                                                    <span data-l10n-id="pdfjs-document-properties-button-label"></span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div id="loadingBar">
                                <div className="progress">
                                    <div className="glimmer">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="viewerContainer" >
                        <div id="viewer" className="pdfViewer"></div>
                    </div>
                </div>

                <div id="dialogContainer">
                    <dialog id="passwordDialog">
                        <div className="row">
                            <label id="passwordText" data-l10n-id="pdfjs-password-label"></label>
                        </div>
                        <div className="row">
                            <input type="password" id="password" className="toolbarField" />
                        </div>
                        <div className="buttonRow">
                            <button id="passwordCancel" className="dialogButton" type="button"><span data-l10n-id="pdfjs-password-cancel-button"></span></button>
                            <button id="passwordSubmit" className="dialogButton" type="button"><span data-l10n-id="pdfjs-password-ok-button"></span></button>
                        </div>
                    </dialog>
                    <dialog id="documentPropertiesDialog">
                        <div className="row">
                            <span id="fileNameLabel" data-l10n-id="pdfjs-document-properties-file-name"></span>
                            <p id="fileNameField" aria-labelledby="fileNameLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="fileSizeLabel" data-l10n-id="pdfjs-document-properties-file-size"></span>
                            <p id="fileSizeField" aria-labelledby="fileSizeLabel">-</p>
                        </div>
                        <div className="separator"></div>
                        <div className="row">
                            <span id="titleLabel" data-l10n-id="pdfjs-document-properties-title"></span>
                            <p id="titleField" aria-labelledby="titleLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="authorLabel" data-l10n-id="pdfjs-document-properties-author"></span>
                            <p id="authorField" aria-labelledby="authorLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="subjectLabel" data-l10n-id="pdfjs-document-properties-subject"></span>
                            <p id="subjectField" aria-labelledby="subjectLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="keywordsLabel" data-l10n-id="pdfjs-document-properties-keywords"></span>
                            <p id="keywordsField" aria-labelledby="keywordsLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="creationDateLabel" data-l10n-id="pdfjs-document-properties-creation-date"></span>
                            <p id="creationDateField" aria-labelledby="creationDateLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="modificationDateLabel" data-l10n-id="pdfjs-document-properties-modification-date"></span>
                            <p id="modificationDateField" aria-labelledby="modificationDateLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="creatorLabel" data-l10n-id="pdfjs-document-properties-creator"></span>
                            <p id="creatorField" aria-labelledby="creatorLabel">-</p>
                        </div>
                        <div className="separator"></div>
                        <div className="row">
                            <span id="producerLabel" data-l10n-id="pdfjs-document-properties-producer"></span>
                            <p id="producerField" aria-labelledby="producerLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="versionLabel" data-l10n-id="pdfjs-document-properties-version"></span>
                            <p id="versionField" aria-labelledby="versionLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="pageCountLabel" data-l10n-id="pdfjs-document-properties-page-count"></span>
                            <p id="pageCountField" aria-labelledby="pageCountLabel">-</p>
                        </div>
                        <div className="row">
                            <span id="pageSizeLabel" data-l10n-id="pdfjs-document-properties-page-size"></span>
                            <p id="pageSizeField" aria-labelledby="pageSizeLabel">-</p>
                        </div>
                        <div className="separator"></div>
                        <div className="row">
                            <span id="linearizedLabel" data-l10n-id="pdfjs-document-properties-linearized"></span>
                            <p id="linearizedField" aria-labelledby="linearizedLabel">-</p>
                        </div>
                        <div className="buttonRow">
                            <button id="documentPropertiesClose" className="dialogButton" type="button"><span data-l10n-id="pdfjs-document-properties-close-button"></span></button>
                        </div>
                    </dialog>
                    <dialog className="dialog" id="altTextDialog" aria-labelledby="dialogLabel" aria-describedby="dialogDescription">
                        <div id="altTextContainer" className="mainContainer">
                            <div id="overallDescription">
                                <span id="dialogLabel" data-l10n-id="pdfjs-editor-alt-text-dialog-label" className="title"></span>
                                <span id="dialogDescription" data-l10n-id="pdfjs-editor-alt-text-dialog-description"></span>
                            </div>
                            <div id="addDescription">
                                <div className="radio">
                                    <div className="radioButton">
                                        <input type="radio" id="descriptionButton" name="altTextOption" aria-describedby="descriptionAreaLabel" checked />
                                        <label data-l10n-id="pdfjs-editor-alt-text-add-description-label"></label>
                                    </div>
                                    <div className="radioLabel">
                                        <span id="descriptionAreaLabel" data-l10n-id="pdfjs-editor-alt-text-add-description-description"></span>
                                    </div>
                                </div>
                                <div className="descriptionArea">
                                    <textarea id="descriptionTextarea" aria-labelledby="descriptionAreaLabel" data-l10n-id="pdfjs-editor-alt-text-textarea" ></textarea>
                                </div>
                            </div>
                            <div id="markAsDecorative">
                                <div className="radio">
                                    <div className="radioButton">
                                        <input type="radio" id="decorativeButton" name="altTextOption" aria-describedby="decorativeLabel" />
                                        <label data-l10n-id="pdfjs-editor-alt-text-mark-decorative-label"></label>
                                    </div>
                                    <div className="radioLabel">
                                        <span id="decorativeLabel" data-l10n-id="pdfjs-editor-alt-text-mark-decorative-description"></span>
                                    </div>
                                </div>
                            </div>
                            <div id="buttons">
                                <button id="altTextCancel" className="secondaryButton" type="button" ><span data-l10n-id="pdfjs-editor-alt-text-cancel-button"></span></button>
                                <button id="altTextSave" className="primaryButton" type="button" ><span data-l10n-id="pdfjs-editor-alt-text-save-button"></span></button>
                            </div>
                        </div>
                    </dialog>
                    <dialog className="dialog newAltText" id="newAltTextDialog" aria-labelledby="newAltTextTitle" aria-describedby="newAltTextDescription" >
                        <div id="newAltTextContainer" className="mainContainer">
                            <div className="title">
                                <span id="newAltTextTitle" data-l10n-id="pdfjs-editor-new-alt-text-dialog-edit-label" role="sectionhead" ></span>
                            </div>
                            <div id="mainContent">
                                <div id="descriptionAndSettings">
                                    <div id="descriptionInstruction">
                                        <div id="newAltTextDescriptionContainer">
                                            <div className="altTextSpinner" role="status" aria-live="polite"></div>
                                            <textarea id="newAltTextDescriptionTextarea" aria-labelledby="descriptionAreaLabel" data-l10n-id="pdfjs-editor-new-alt-text-textarea" ></textarea>
                                        </div>
                                        <span id="newAltTextDescription" role="note" data-l10n-id="pdfjs-editor-new-alt-text-description"></span>
                                        <div id="newAltTextDisclaimer" role="note"><div><span data-l10n-id="pdfjs-editor-new-alt-text-disclaimer1"></span> <a href="https://support.mozilla.org/en-US/kb/pdf-alt-text" target="_blank" rel="noopener noreferrer" id="newAltTextLearnMore" data-l10n-id="pdfjs-editor-new-alt-text-disclaimer-learn-more-url" ></a></div></div>
                                    </div>
                                    <div id="newAltTextCreateAutomatically" className="toggler">
                                        <button id="newAltTextCreateAutomaticallyButton" className="toggle-button" type="button" aria-pressed="true" ></button>
                                        <label className="togglerLabel" data-l10n-id="pdfjs-editor-new-alt-text-create-automatically-button-label"></label>
                                    </div>
                                    <div id="newAltTextDownloadModel" className="hidden">
                                        <span id="newAltTextDownloadModelDescription" data-l10n-id="pdfjs-editor-new-alt-text-ai-model-downloading-progress" data-l10n-args='{ "totalSize": 0, "downloadedSize": 0 }'></span>
                                    </div>
                                </div>
                                <div id="newAltTextImagePreview"></div>
                            </div>
                            <div id="newAltTextError" className="messageBar">
                                <div>
                                    <div>
                                        <span className="title" data-l10n-id="pdfjs-editor-new-alt-text-error-title"></span>
                                        <span className="description" data-l10n-id="pdfjs-editor-new-alt-text-error-description"></span>
                                    </div>
                                    <button id="newAltTextCloseButton" className="closeButton" type="button" ><span data-l10n-id="pdfjs-editor-new-alt-text-error-close-button"></span></button>
                                </div>
                            </div>
                            <div id="newAltTextButtons" className="dialogButtonsGroup">
                                <button id="newAltTextCancel" type="button" className="secondaryButton hidden" ><span data-l10n-id="pdfjs-editor-alt-text-cancel-button"></span></button>
                                <button id="newAltTextNotNow" type="button" className="secondaryButton" ><span data-l10n-id="pdfjs-editor-new-alt-text-not-now-button"></span></button>
                                <button id="newAltTextSave" type="button" className="primaryButton" ><span data-l10n-id="pdfjs-editor-alt-text-save-button"></span></button>
                            </div>
                        </div>
                    </dialog>

                    <dialog className="dialog" id="altTextSettingsDialog" aria-labelledby="altTextSettingsTitle">
                        <div id="altTextSettingsContainer" className="mainContainer">
                            <div className="title">
                                <span id="altTextSettingsTitle" data-l10n-id="pdfjs-editor-alt-text-settings-dialog-label" role="sectionhead" className="title"></span>
                            </div>
                            <div id="automaticAltText">
                                <span data-l10n-id="pdfjs-editor-alt-text-settings-automatic-title"></span>
                                <div id="automaticSettings">
                                    <div id="createModelSetting">
                                        <div className="toggler">
                                            <button id="createModelButton" type="button" className="toggle-button" aria-pressed="true" ></button>
                                            <label className="togglerLabel" data-l10n-id="pdfjs-editor-alt-text-settings-create-model-button-label"></label>
                                        </div>
                                        <div id="createModelDescription" className="description">
                                            <span data-l10n-id="pdfjs-editor-alt-text-settings-create-model-description"></span> <a href="https://support.mozilla.org/en-US/kb/pdf-alt-text" target="_blank" rel="noopener noreferrer" id="altTextSettingsLearnMore" data-l10n-id="pdfjs-editor-new-alt-text-disclaimer-learn-more-url" ></a>
                                        </div>
                                    </div>
                                    <div id="aiModelSettings">
                                        <div>
                                            <span data-l10n-id="pdfjs-editor-alt-text-settings-download-model-label" data-l10n-args='{ "totalSize": 180 }'></span>
                                            <div id="aiModelDescription" className="description">
                                                <span data-l10n-id="pdfjs-editor-alt-text-settings-ai-model-description"></span>
                                            </div>
                                        </div>
                                        <button id="deleteModelButton" type="button" className="secondaryButton" ><span data-l10n-id="pdfjs-editor-alt-text-settings-delete-model-button"></span></button>
                                        <button id="downloadModelButton" type="button" className="secondaryButton" ><span data-l10n-id="pdfjs-editor-alt-text-settings-download-model-button"></span></button>
                                    </div>
                                </div>
                            </div>
                            <div className="dialogSeparator"></div>
                            <div id="altTextEditor">
                                <span data-l10n-id="pdfjs-editor-alt-text-settings-editor-title"></span>
                                <div id="showAltTextEditor">
                                    <div className="toggler">
                                        <button id="showAltTextDialogButton" type="button" className="toggle-button" aria-pressed="true" ></button>
                                        <label className="togglerLabel" data-l10n-id="pdfjs-editor-alt-text-settings-show-dialog-button-label"></label>
                                    </div>
                                    <div id="showAltTextDialogDescription" className="description">
                                        <span data-l10n-id="pdfjs-editor-alt-text-settings-show-dialog-description"></span>
                                    </div>
                                </div>
                            </div>
                            <div id="buttons" className="dialogButtonsGroup">
                                <button id="altTextSettingsCloseButton" type="button" className="primaryButton" ><span data-l10n-id="pdfjs-editor-alt-text-settings-close-button"></span></button>
                            </div>
                        </div>
                    </dialog>

                    <dialog className="dialog signatureDialog" id="addSignatureDialog" aria-labelledby="addSignatureDialogLabel">
                        <span id="addSignatureDialogLabel" data-l10n-id="pdfjs-editor-add-signature-dialog-label"></span>
                        <div id="addSignatureContainer" className="mainContainer">
                            <div className="title">
                                <span role="sectionhead" data-l10n-id="pdfjs-editor-add-signature-dialog-title" ></span>
                            </div>
                            <div role="tablist" id="addSignatureOptions">
                                <button id="addSignatureTypeButton" type="button" role="tab" aria-selected="true" aria-controls="addSignatureTypeContainer" data-l10n-id="pdfjs-editor-add-signature-type-button" ></button>
                                <button id="addSignatureDrawButton" type="button" role="tab" aria-selected="false" aria-controls="addSignatureDrawContainer" data-l10n-id="pdfjs-editor-add-signature-draw-button" ></button>
                                <button id="addSignatureImageButton" type="button" role="tab" aria-selected="false" aria-controls="addSignatureImageContainer" data-l10n-id="pdfjs-editor-add-signature-image-button" ></button>
                            </div>
                            <div id="addSignatureActionContainer" data-selected="type">
                                <div id="addSignatureTypeContainer" role="tabpanel" aria-labelledby="addSignatureTypeContainer">
                                    <input id="addSignatureTypeInput" type="text" data-l10n-id="pdfjs-editor-add-signature-type-input" ></input>
                                </div>
                                <div id="addSignatureDrawContainer" role="tabpanel" aria-labelledby="addSignatureDrawButton" >
                                    <svg id="addSignatureDraw" xmlns="http://www.w3.org/2000/svg" aria-labelledby="addSignatureDrawPlaceholder"></svg>
                                    <span id="addSignatureDrawPlaceholder" data-l10n-id="pdfjs-editor-add-signature-draw-placeholder"></span>
                                    <div id="thickness">
                                        <div>
                                            <label data-l10n-id="pdfjs-editor-add-signature-draw-thickness-range-label"></label>
                                            <input type="range" id="addSignatureDrawThickness" min="1" max="5" step="1" value="1" data-l10n-id="pdfjs-editor-add-signature-draw-thickness-range" data-l10n-args='{ "thickness": 1 }' />
                                        </div>
                                    </div>
                                </div>
                                <div id="addSignatureImageContainer" role="tabpanel" aria-labelledby="addSignatureImageButton" >
                                    <svg id="addSignatureImage" xmlns="http://www.w3.org/2000/svg" aria-labelledby="addSignatureImagePlaceholder"></svg>
                                    <div id="addSignatureImagePlaceholder">
                                        <span data-l10n-id="pdfjs-editor-add-signature-image-placeholder"></span>
                                        <label id="addSignatureImageBrowse" >
                                            <a data-l10n-id="pdfjs-editor-add-signature-image-browse-link"></a>
                                        </label>
                                        <input id="addSignatureFilePicker" type="file"></input>
                                    </div>
                                </div>
                                <div id="addSignatureControls">
                                    <div id="horizontalContainer">
                                        <div id="addSignatureDescriptionContainer">
                                            <label data-l10n-id="pdfjs-editor-add-signature-description-label"></label>
                                            <span id="addSignatureDescription" className="inputWithClearButton">
                                                <input id="addSignatureDescInput" type="text" data-l10n-id="pdfjs-editor-add-signature-description-input" ></input>
                                                <button className="clearInputButton" type="button" aria-hidden></button>
                                            </span>
                                        </div>
                                        <button id="clearSignatureButton" type="button" data-l10n-id="pdfjs-editor-add-signature-clear-button" ><span data-l10n-id="pdfjs-editor-add-signature-clear-button-label"></span></button>
                                    </div>
                                    <div id="addSignatureSaveContainer">
                                        <input type="checkbox" id="addSignatureSaveCheckbox" checked></input>
                                        <label data-l10n-id="pdfjs-editor-add-signature-save-checkbox"></label>
                                        <span></span>
                                        <span id="addSignatureSaveWarning" data-l10n-id="pdfjs-editor-add-signature-save-warning-message"></span>
                                    </div>
                                </div>
                                <div id="addSignatureError" hidden className="messageBar">
                                    <div>
                                        <div>
                                            <span className="title" data-l10n-id="pdfjs-editor-add-signature-image-upload-error-title"></span>
                                            <span className="description" data-l10n-id="pdfjs-editor-add-signature-image-upload-error-description"></span>
                                        </div>
                                        <button id="addSignatureErrorCloseButton" className="closeButton" type="button" ><span data-l10n-id="pdfjs-editor-add-signature-error-close-button"></span></button>
                                    </div>
                                </div>
                                <div className="dialogButtonsGroup">
                                    <button id="addSignatureCancelButton" type="button" className="secondaryButton" ><span data-l10n-id="pdfjs-editor-add-signature-cancel-button"></span></button>
                                    <button id="addSignatureAddButton" type="button" className="primaryButton" disabled ><span data-l10n-id="pdfjs-editor-add-signature-add-button"></span></button>
                                </div>
                            </div>
                        </div>
                    </dialog>

                    <dialog className="dialog signatureDialog" id="editSignatureDescriptionDialog" aria-labelledby="editSignatureDescriptionTitle">
                        <div id="editSignatureDescriptionContainer" className="mainContainer">
                            <div className="title">
                                <span id="editSignatureDescriptionTitle" role="sectionhead" data-l10n-id="pdfjs-editor-edit-signature-dialog-title" ></span>
                            </div>
                            <div id="editSignatureDescriptionAndView">
                                <div id="editSignatureDescriptionContainer">
                                    <label data-l10n-id="pdfjs-editor-add-signature-description-label"></label>
                                    <span id="editSignatureDescription" className="inputWithClearButton">
                                        <input id="editSignatureDescInput" type="text" data-l10n-id="pdfjs-editor-add-signature-description-input" ></input>
                                        <button className="clearInputButton" type="button" aria-hidden></button>
                                    </span>
                                </div>
                                <svg id="editSignatureView" xmlns="http://www.w3.org/2000/svg"></svg>
                            </div>
                            <div className="dialogButtonsGroup">
                                <button id="editSignatureCancelButton" type="button" className="secondaryButton" ><span data-l10n-id="pdfjs-editor-add-signature-cancel-button"></span></button>
                                <button id="editSignatureUpdateButton" type="button" className="primaryButton" disabled ><span data-l10n-id="pdfjs-editor-edit-signature-update-button"></span></button>
                            </div>
                        </div>
                    </dialog>

                    <dialog id="printServiceDialog" style={{ minWidth: 200 }}>
                        <div className="row">
                            <span data-l10n-id="pdfjs-print-progress-message"></span>
                        </div>
                        <div className="row">
                            <progress value="0" max="100"></progress>
                            <span data-l10n-id="pdfjs-print-progress-percent" data-l10n-args='{ "progress": 0 }' className="relative-progress">0%</span>
                        </div>
                        <div className="buttonRow">
                            <button id="printCancel" className="dialogButton" type="button"><span data-l10n-id="pdfjs-print-progress-close-button"></span></button>
                        </div>
                    </dialog>
                </div>

                <div id="editorUndoBar" className="messageBar" role="status" aria-labelledby="editorUndoBarMessage" hidden>
                    <div>
                        <div>
                            <span id="editorUndoBarMessage" className="description"></span>
                        </div>
                        <button id="editorUndoBarUndoButton" className="undoButton" type="button" data-l10n-id="pdfjs-editor-undo-bar-undo-button">
                            <span data-l10n-id="pdfjs-editor-undo-bar-undo-button-label"></span>
                        </button>
                        <button id="editorUndoBarCloseButton" className="closeButton" type="button" data-l10n-id="pdfjs-editor-undo-bar-close-button">
                            <span data-l10n-id="pdfjs-editor-undo-bar-close-button-label"></span>
                        </button>
                    </div>
                </div>

            </div>
            <div id="printContainer"></div>
        </>
    );
};
